/* eslint-disable max-lines-per-function */
import { requestPayment } from 'Cloud/Application/Payment';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { uploadFileSizeLimitMbAb } from 'reactApp/appHelpers/featuresHelpers';
import { abHeavyFileBuy, abHeavyFileBuyV3 } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import { uploadBigFileModal } from 'reactApp/appHelpers/featuresHelpers/features/personalTariffsModal';
import { uploadBigFileNewFreePeriod } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileModal';
import { type IModalCallbacks, type TariffInfo, EShowTariffType } from 'reactApp/components/UploadBigFileDialog/UploadBigFileDialog.types';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { getFreemiumHeavyFileAnalyticsName, sendFreemiumHeavyFileDwh } from 'reactApp/ui/Freemium/freemiumHeavyFile.helpers';
import { openFreemiumHeavyFileModal } from 'reactApp/ui/Freemium/openFreemiumHeavyFileModal';
import { useShouldShowFreemiumHeavyFile } from 'reactApp/ui/Freemium/useShouldShowFreemiumHeavyFile';
import { EPaymentGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

const BUY_TARIFF = 'buy-tariff';
const PAY_SOURCE = 'cloud_popup_heavy_file';
const PAY_SOURCE_TRANSACT = 'cloud_popup_heavy_file_onetime';
const RADAR_NAME = 'mnz_cloud_popup_heavy_file';

export const useBigFileModalCallbacks = ({
    stage,
    closeModal,
    skipFileLoading,
    continueFileLoading,
    sendGa,
    tariffInfo,
    fileSizeBytes,
    nextStage,
}): IModalCallbacks => {
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const paidLessThanNewFreeMonthPeriod = useSelector((state: RootState) =>
        PaidInfoSelectors.lastPaymentWasLessThan(state, { months: uploadBigFileNewFreePeriod })
    );
    const dispatch = useDispatch();
    const shouldShowFreemium = useShouldShowFreemiumHeavyFile();

    let paySource = PAY_SOURCE;
    let paySourceTransact = PAY_SOURCE_TRANSACT;
    let radarName = RADAR_NAME;
    /* tempexp_18955-start */
    if (abHeavyFileBuyV3) {
        paySource += '_v3';
        paySourceTransact += '_v3';
        radarName += '_v3';
    }
    if (abHeavyFileBuyV3 === 'd') {
        paySource += '_img';
        paySourceTransact += '_img';
        radarName += '_img';
    }
    /* tempexp_18955-end */

    const { trial, usualProduct, transactProduct, showTariffType } = <TariffInfo>tariffInfo;

    const sendPayment = useCallback(
        (label: string, noSuffix: boolean = false) => {
            // tempexp_18955-start
            const calculatedLabel = !noSuffix && abHeavyFileBuyV3 && stage === 'transact' ? `${label}-onetime` : label;
            const productId = (stage === 'transact' ? tariffInfo.transactProduct : tariffInfo.usualProduct)?.id;
            // tempexp_18955-end
            sendPaymentGa({
                action: EPaymentGa.bigFileModal,
                label: calculatedLabel,
                size_offer: fileSizeBytes,
                user_status: isPaidUser ? 'paid' : paidLessThanNewFreeMonthPeriod ? 'new_free' : 'free',
                ...(abHeavyFileBuyV3 ? { name: radarName, tariff: productId } : {}),
            });

            if (shouldShowFreemium && (label === 'close' || label === 'view')) {
                const analyticsName = getFreemiumHeavyFileAnalyticsName();
                const action = label === 'close' ? 'close' : 'show';
                sendFreemiumHeavyFileDwh(action, analyticsName);
            }
        },
        [fileSizeBytes, paidLessThanNewFreeMonthPeriod, isPaidUser, stage, tariffInfo, radarName, shouldShowFreemium]
    );

    const onSkip = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_CANCEL, { stage });
        sendGa('skip', showTariffType);
        sendPayment('cancel');
        skipFileLoading();
        closeModal();
    }, [showTariffType, stage]); // closeModal передается из старого кода, коллбек пересоздавать не нужно при его изменении

    const onClose = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_CLOSE, { stage });
        sendGa('close', showTariffType);
        sendPayment('close');

        // tempexp_18655-start
        if (abHeavyFileBuyV3 && stage === 'usual') {
            nextStage();
            return;
        }
        // tempexp_18655-end
        skipFileLoading();
        closeModal();
    }, [showTariffType, stage]);

    const onSuccess = (gaLabel: 'buy-trial' | 'buy-tariff') => () => {
        closeModal();
        sendGa(gaLabel);

        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_PAYMENT_BUY, {
            tariff: gaLabel === 'buy-trial' ? 'trial' : 'tariff',
        });
    };

    const onRequestPayment = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_CLICK, { stage });

        // tempexp_17018-start
        const query = uploadFileSizeLimitMbAb
            ? 'utm_source=cloud&utm_medium=web_union_quota_landing&utm_campaign=popup_heavy_file&utm_term=17018'
            : undefined;
        // tempexp_17018-end

        sendGa('request-payment', showTariffType);
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_PAYMENT_SHOW);
        sendPayment('click');
        // Пользователю с неактивированным триалом в 128ГБ
        // показываем окно с предложением оплатить триал.
        // Оверквотеру триал не показываем.
        if (showTariffType === EShowTariffType.TRIAL) {
            requestPayment({
                productId: trial?.id,
                forceFastCheckout: true,
                onSuccess: onSuccess('buy-trial'),
                afterUpdate: continueFileLoading,
                paySource,
                // tempexp_17018-next-line
                query,
            });

            return;
        }

        // Флаг forceFastCheckout не работает, а работает так:
        // если id продукта передали пропом tariff, то точно откроется
        // фастчекаут (то есть как будто есть работающий forceFastCheckout).
        // Как починят, можно убрать
        /* tempexp_18869-start tempexp_18955-start */
        if (abHeavyFileBuy || abHeavyFileBuyV3) {
            requestPayment({
                onSuccess: onSuccess(BUY_TARIFF),
                afterUpdate: continueFileLoading,
                productId: usualProduct?.id,
                paySource,
            });
            return;
        }
        /* tempexp_18869-end tempexp_18955-end */

        requestPayment({
            forceFastCheckout: true,
            onSuccess: onSuccess(BUY_TARIFF),
            afterUpdate: continueFileLoading,
            tariff: usualProduct?.id,
            paySource,
            // tempexp_17018-next-line
            query,
        });
    }, [showTariffType, usualProduct?.id, trial?.id, onSuccess, stage]);

    /* tempexp_18869-start tempexp_18955-start */
    const onRequestHeavyFile = useCallback(() => {
        sendPayment('click-onetime', true);
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_CLICK_BUY_ONCE);
        if (showTariffType === EShowTariffType.TRANSACT) {
            requestPayment({
                onSuccess: onSuccess(BUY_TARIFF),
                afterUpdate: continueFileLoading,
                productId: transactProduct?.id,
                paySource: paySourceTransact,
                title: 'Оплата загрузки одного файла',
                description: `Загрузка одного файла до 100 ГБ в пределах свободного места`,
            });
        }
    }, [onSuccess]);
    /* tempexp_18869-end tempexp_18955-end */

    const onClickBuy = useTariffBuy({
        paySource: uploadBigFileModal.paySource,
    });

    const onShowModal = useCallback(() => {
        sendPayment('click');
        emitAnalyticEvent(AnalyticEventNames.BIG_FILE_DIALOG_CLICK);

        dispatch(
            openBuyDisableAds({
                title: uploadBigFileModal.buyTitle,
                tariffsNumber: uploadBigFileModal.tariffsNumber,
                activeTab: uploadBigFileModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                name: uploadBigFileModal.name,
                actionShow: uploadBigFileModal.actionShow,
                actionClick: uploadBigFileModal.actionClick,
                modal: 'heavy_file',
            })
        );
    }, [dispatch, uploadBigFileModal]);

    const onRequestFreemium = useCallback(() => {
        if (!shouldShowFreemium) {
            return;
        }

        const analyticsName = getFreemiumHeavyFileAnalyticsName();
        sendFreemiumHeavyFileDwh('click', analyticsName);

        openFreemiumHeavyFileModal(dispatch);

        skipFileLoading();
        closeModal();
    }, [dispatch, closeModal, skipFileLoading, shouldShowFreemium]);

    return {
        onSkip,
        onRequestPayment,
        onClose,
        sendAnalytics: sendPayment,
        // tempexp_18869-next-line
        onRequestHeavyFile,
        onRequestFreemium: shouldShowFreemium ? onRequestFreemium : undefined,
        // tempexp_18603-next-line
        proposedTariff: showTariffType === EShowTariffType.TRIAL ? trial : usualProduct,
        onShowModal,
    };
};
